import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatarDataHora } from '../utils/formatters';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';

export default function ApiKeysPage() {
  const [keys,        setKeys]        = useState([]);
  const [empresas,    setEmpresas]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [novaChave,   setNovaChave]   = useState(null);  // chave completa exibida após criação
  const [salvando,    setSalvando]    = useState(false);
  const [confirmRev,  setConfirmRev]  = useState(null);  // key a revogar
  const [form, setForm] = useState({ empresa_id: '', nome: '', expira_em: '', tipo: 'empresa' });

  useEffect(() => {
    Promise.all([
      api.get('/api-keys?empresa_id=all').catch(() => ({ data: [] })),
      api.get('/empresas')
    ]).then(([rKeys, rEmpresas]) => {
      setKeys(rKeys.data);
      setEmpresas(rEmpresas.data);
    }).finally(() => setLoading(false));
  }, []);

  async function criarKey() {
    if (!form.nome) return toast.error('Nome é obrigatório.');
    if (form.tipo === 'empresa' && !form.empresa_id) return toast.error('Selecione a empresa.');
    setSalvando(true);
    try {
      const { data } = await api.post('/api-keys', form);
      setNovaChave(data.chave_completa);
      setKeys(ks => [...ks, data]);
      setModalAberto(false);
      setForm({ empresa_id: '', nome: '', expira_em: '', tipo: 'empresa' });
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao criar API Key.');
    } finally {
      setSalvando(false);
    }
  }

  async function revogar() {
    if (!confirmRev) return;
    try {
      await api.delete(`/api-keys/${confirmRev.id}`);
      setKeys(ks => ks.map(k => k.id === confirmRev.id ? { ...k, ativa: false } : k));
      toast.success('API Key revogada com sucesso.');
    } catch {
      toast.error('Erro ao revogar chave.');
    } finally {
      setConfirmRev(null);
    }
  }

  async function copiar(texto) {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Chave copiada para a área de transferência!');
    } catch {
      toast.error('Não foi possível copiar. Copie manualmente.');
    }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-primary-500" /></div>;

  return (
    <div className="space-y-4">
      {/* Alerta de como usar no Delphi */}
      <div className="card bg-amber-900/10 border-amber-800/30">
        <div className="flex gap-3">
          <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm space-y-2">
            <p className="font-semibold text-amber-300">Integração com ERP Delphi</p>
            <p className="text-amber-200/60 text-xs leading-relaxed">
              Use o header <code className="bg-amber-900/40 px-1.5 py-0.5 rounded font-mono text-amber-200">X-API-Key: sua_chave</code>{' '}
              no endpoint <code className="bg-amber-900/40 px-1.5 py-0.5 rounded font-mono text-amber-200">POST /api/integracao/documento</code>.
            </p>
            <div className="text-xs text-amber-200/70 flex flex-col gap-1">
              <span><strong className="text-amber-300">Chave de empresa:</strong> vinculada a um CNPJ específico.</span>
              <span><strong className="text-amber-300">Chave de sistema:</strong> uma chave para todos os CNPJs — o Storage detecta a empresa pelo CNPJ do emitente no XML automaticamente.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chave recém criada */}
      {novaChave && (
        <div className="card bg-emerald-900/10 border-emerald-700/30">
          <div className="flex items-start gap-2 mb-3">
            <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-300">Chave criada com sucesso!</p>
              <p className="text-xs text-emerald-200/60 mt-0.5">⚠️ Esta chave só é exibida uma vez. Copie agora e guarde em lugar seguro.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-surface rounded-lg p-3">
            <code className="font-mono text-xs text-emerald-300 flex-1 break-all">{novaChave}</code>
            <button onClick={() => copiar(novaChave)}
              className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-surface-card transition-colors">
              <Copy size={15} />
            </button>
          </div>
          <button onClick={() => setNovaChave(null)} className="text-xs text-slate-600 hover:text-slate-400 mt-2 transition-colors">
            Já copiei, fechar
          </button>
        </div>
      )}

      {/* Ação */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{keys.length} chave(s) cadastrada(s)</p>
        <button onClick={() => setModalAberto(true)} className="btn-primary text-sm py-2">
          <Plus size={15} /> Nova API Key
        </button>
      </div>

      {/* Tabela */}
      <div className="card p-0 overflow-hidden">
        {keys.length === 0 ? (
          <div className="text-center py-12 text-slate-600">
            <Key size={32} className="mx-auto mb-2 opacity-30" />
            <p>Nenhuma API Key cadastrada ainda.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border bg-surface/40">
                {['Nome', 'Tipo / Empresa', 'Prefixo', 'Situação', 'Último uso', 'Expira em', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {keys.map(k => {
                const empresa = empresas.find(e => e.id === k.empresa_id);
                return (
                  <tr key={k.id} className="hover:bg-surface-card/50 transition-colors">
                    <td className="px-4 py-3 text-slate-200 font-medium">{k.nome}</td>
                    <td className="px-4 py-3 text-xs">
                      {k.tipo === 'sistema'
                        ? <span className="px-2 py-0.5 rounded-full bg-violet-900/40 text-violet-300">Sistema — todas as empresas</span>
                        : <span className="text-slate-400">{empresa?.razao_social || k.empresa_id}</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <code className="font-mono text-xs text-primary-400 bg-primary-900/20 px-2 py-0.5 rounded">
                        {k.prefixo}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${k.ativa
                        ? 'bg-emerald-900/40 text-emerald-400'
                        : 'bg-red-900/40 text-red-400'}`}>
                        {k.ativa ? 'Ativa' : 'Revogada'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatarDataHora(k.ultimo_uso) || 'Nunca usada'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatarDataHora(k.expira_em) || 'Sem expiração'}</td>
                    <td className="px-4 py-3 text-right">
                      {k.ativa && (
                        <button onClick={() => setConfirmRev(k)}
                          title="Revogar chave"
                          className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-900/20 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal nova chave */}
      <Modal aberto={modalAberto} onFechar={() => setModalAberto(false)} titulo="Nova API Key">
        <div className="space-y-4">
          {/* Tipo */}
          <div>
            <label className="label">Tipo de chave</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: 'sistema',  l: 'Sistema',  sub: 'Uma chave para todos os CNPJs' },
                { v: 'empresa',  l: 'Empresa',  sub: 'Vinculada a um CNPJ específico' },
              ].map(({ v, l, sub }) => (
                <button key={v} type="button"
                  onClick={() => setForm(f => ({ ...f, tipo: v, empresa_id: '' }))}
                  className={`text-left p-3 rounded-xl border transition-all ${form.tipo === v
                    ? 'border-primary-500 bg-primary-900/20 text-primary-300'
                    : 'border-surface-border text-slate-400 hover:border-slate-600'}`}>
                  <p className="text-sm font-medium">{l}</p>
                  <p className="text-xs opacity-70 mt-0.5">{sub}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Empresa (apenas tipo=empresa) */}
          {form.tipo === 'empresa' && (
            <div>
              <label className="label">Empresa</label>
              <select className="input" value={form.empresa_id} onChange={e => setForm(f => ({...f, empresa_id: e.target.value}))}>
                <option value="">Selecione uma empresa...</option>
                {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">Nome da chave</label>
            <input className="input" value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))}
              placeholder='Ex: "ERP Produção", "ERP Homologação"' />
          </div>
          <div>
            <label className="label">Data de expiração (opcional)</label>
            <input className="input" type="date" value={form.expira_em} onChange={e => setForm(f => ({...f, expira_em: e.target.value}))} />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => setModalAberto(false)} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
            <button onClick={criarKey} disabled={salvando} className="btn-primary text-sm py-2 px-4">
              {salvando ? <><Loader2 size={14} className="animate-spin" /> Gerando...</> : <><Key size={14} /> Gerar Chave</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm revogar */}
      <ConfirmDialog
        aberto={!!confirmRev}
        onFechar={() => setConfirmRev(null)}
        onConfirmar={revogar}
        titulo="Revogar API Key"
        mensagem={`Deseja revogar a chave "${confirmRev?.nome}"? O ERP Delphi não conseguirá mais enviar documentos com esta chave. Esta ação não pode ser desfeita.`}
        textoBotao="Revogar Chave"
      />
    </div>
  );
}
